import { query } from "../../erd-editor-schema/dist/query/index.js";
import { ColumnUIKey } from "../../constants/schema.js";
import { observable } from "../../r-html/dist/observable/index.js";
import { bHas } from "../../utils/bit.js";
import { ShowMode } from "../../engine/modules/editor/state.js";
import { getSourceView } from "../../engine/modules/editor/view.js";
import { isMemoShown } from "../../utils/memo.js";
//#region src/konva/scene/viewLayout.ts
var NONE = Object.freeze({
	tableIds: [],
	memoIds: [],
	relationshipIds: [],
	tableGroupIds: []
});
var DIM_OPACITY = .2;
function getTablePoint(state, table, source = "document") {
	const { x, y } = getSourceView(state, source)?.positions[table.id] ?? table.ui;
	return {
		x,
		y
	};
}
var isKeyColumn = (keys) => bHas(keys, ColumnUIKey.primaryKey) || bHas(keys, ColumnUIKey.foreignKey);
function forEachRelationshipEnd(state, visit) {
	const relationships = query(state.collections).collection("relationshipEntities").selectByIds(state.doc.relationshipIds);
	for (const { start, end } of relationships) {
		visit(start.tableId, start.columnIds);
		visit(end.tableId, end.columnIds);
	}
}
function relationshipColumnIds(state, table) {
	const ids = /* @__PURE__ */ new Set();
	forEachRelationshipEnd(state, (tableId, columnIds) => {
		if (tableId !== table.id) return;
		columnIds.forEach((id) => ids.add(id));
	});
	return ids;
}
function relationshipColumnIdsByTable(state) {
	const byTable = /* @__PURE__ */ new Map();
	forEachRelationshipEnd(state, (tableId, columnIds) => {
		const ids = byTable.get(tableId) ?? /* @__PURE__ */ new Set();
		columnIds.forEach((id) => ids.add(id));
		byTable.set(tableId, ids);
	});
	return byTable;
}
function getVisibleColumnIds(state, table, source = "document") {
	const view = getSourceView(state, source);
	if (!view || view.showMode === ShowMode.allFields) return table.columnIds;
	if (view.showMode === ShowMode.nameOnly) return [];
	const related = relationshipColumnIds(state, table);
	return query(state.collections).collection("tableColumnEntities").selectByIds(table.columnIds).filter((column) => isKeyColumn(column.ui.keys) || related.has(column.id)).map((column) => column.id);
}
function reach(centerIds, relationships) {
	const centers = new Set(centerIds);
	const reached = new Set(centers);
	for (const { start, end } of relationships) {
		if (centers.has(start.tableId)) reached.add(end.tableId);
		if (centers.has(end.tableId)) reached.add(start.tableId);
	}
	return reached;
}
function getReachedTableIds(state, centerIds) {
	const { doc, collections } = state;
	const reached = reach(centerIds, query(collections).collection("relationshipEntities").selectByIds(doc.relationshipIds));
	return doc.tableIds.filter((id) => reached.has(id));
}
function getVisibleIds(state, source = "document") {
	const { doc, collections } = state;
	if (source === "document") return {
		tableIds: doc.tableIds,
		memoIds: isMemoShown(state) ? doc.memoIds : [],
		relationshipIds: doc.relationshipIds,
		tableGroupIds: doc.tableGroupIds
	};
	const view = getSourceView(state, source);
	if (!view) return NONE;
	const relationships = query(collections).collection("relationshipEntities").selectByIds(doc.relationshipIds);
	const shown = new Set(Object.keys(view.positions));
	const tableIds = doc.tableIds.filter((id) => shown.has(id));
	const inView = new Set(tableIds);
	return {
		tableIds,
		memoIds: [],
		tableGroupIds: [],
		relationshipIds: relationships.filter(({ start, end }) => inView.has(start.tableId) && inView.has(end.tableId)).map((relationship) => relationship.id)
	};
}
var hover = observable({ tableId: {} });
var hoverView = /* @__PURE__ */ new Map();
function dropHover(editorId) {
	hoverView.delete(editorId);
	Reflect.deleteProperty(hover.tableId, editorId);
}
function setViewHoverTable(root, tableId, source = "document") {
	const view = getSourceView(root, source);
	const { id } = root.editor;
	if (tableId === null || !view) {
		if (hoverView.get(id)?.kind === source) dropHover(id);
		return;
	}
	hoverView.set(id, view);
	hover.tableId[id] = tableId;
}
function clearViewHoverTable(root, tableId, source = "document") {
	const { id } = root.editor;
	if (hover.tableId[id] !== tableId || hoverView.get(id)?.kind !== source) return;
	dropHover(id);
}
function getViewHoverTable(root, source = "document") {
	const { id } = root.editor;
	const held = hover.tableId[id];
	return held !== void 0 && hoverView.get(id) === getSourceView(root, source) ? held : null;
}
var pinned = observable({ tableId: {} });
var pinnedView = /* @__PURE__ */ new Map();
function dropPin(editorId) {
	pinnedView.delete(editorId);
	Reflect.deleteProperty(pinned.tableId, editorId);
}
function setViewPinnedTable(root, tableId, source = "document") {
	const view = getSourceView(root, source);
	if (!view) return;
	const { id } = root.editor;
	if ((pinnedView.get(id) === view ? pinned.tableId[id] : void 0) === tableId) {
		dropPin(id);
		return;
	}
	pinnedView.set(id, view);
	pinned.tableId[id] = tableId;
}
function clearViewPinnedTable(root, source = "document") {
	const view = getSourceView(root, source);
	if (!view) return;
	const { id } = root.editor;
	if (pinnedView.get(id) === view) dropPin(id);
}
function getViewPinnedTable(root, source = "document") {
	const { id } = root.editor;
	const held = pinned.tableId[id];
	return held !== void 0 && pinnedView.get(id) === getSourceView(root, source) ? held : null;
}
function getHighlightIds(state, source = "document") {
	if (!getSourceView(state, source)) return {
		tableIds: /* @__PURE__ */ new Set(),
		relationshipIds: /* @__PURE__ */ new Set()
	};
	const shown = getVisibleIds(state, source);
	const shownTables = new Set(shown.tableIds);
	const hovered = getViewHoverTable(state, source);
	const held = getViewPinnedTable(state, source);
	const lit = /* @__PURE__ */ new Set();
	if (hovered !== null && shownTables.has(hovered)) lit.add(hovered);
	if (held !== null && shownTables.has(held)) lit.add(held);
	const tableIds = new Set(lit);
	const relationshipIds = /* @__PURE__ */ new Set();
	const relationships = query(state.collections).collection("relationshipEntities").selectByIds(shown.relationshipIds);
	for (const { id, start, end } of relationships) {
		if (!lit.has(start.tableId) && !lit.has(end.tableId)) continue;
		relationshipIds.add(id);
		tableIds.add(start.tableId);
		tableIds.add(end.tableId);
	}
	return {
		tableIds,
		relationshipIds
	};
}
//#endregion
export { DIM_OPACITY, clearViewHoverTable, clearViewPinnedTable, getHighlightIds, getReachedTableIds, getTablePoint, getViewHoverTable, getViewPinnedTable, getVisibleColumnIds, getVisibleIds, relationshipColumnIdsByTable, setViewHoverTable, setViewPinnedTable };
